import { adminAvaniRoles, type RoleModel, type SignificantRoleType, type UserModel } from '../models';

/**
 * Determines if a user has admin/avani role
 * @param auth
 */
export const isAdminRole = (auth: UserModel) =>
  auth.roles.some(({ name }) => name.includes('avani') || name === 'systemSuperAdmin');

/**
 * Extract clientId from user
 * @param auth
 */
export const extractClientId = (auth: UserModel): string | null => {
  const clientIds = auth.clients.map(({ id }) => id);
  const venueClientIds = auth.venues.map(({ clientId }) => clientId);
  return clientIds[0] ?? venueClientIds[0] ?? null;
};

/**
 * Extracts significant role type from set of roles (if any)
 * @param roles
 */
export const defineSignificantRole = (roles: RoleModel[]): SignificantRoleType | undefined => {
  if (!roles.length) return;
  if (roles.some(({ name }) => adminAvaniRoles.includes(name))) {
    return 'avaniAdmin';
  } else {
    if (roles.some(({ name }) => name === 'clientAdmin')) {
      return 'clientAdmin';
    } else if (roles.some(({ name }) => name === 'clientRom')) {
      return 'clientRom';
    } else {
      // If VenueUser role selected => set single client select
      return 'venueUser';
    }
  }
};

/**
 * Checks if 2 arrays consist of the same objects, containing id, regardless of duplications and order
 * @param a
 * @param b
 */
export const arraysAsSetsAreEqual = <T extends { id: string }>(a: T[], b: T[]): boolean => {
  const setA = new Set(a.map((item) => item.id));
  const setB = new Set(b.map((item) => item.id));
  if (setA.size !== setB.size) return false;
  for (const id of setA) {
    if (!setB.has(id)) return false;
  }
  return true;
};

/**
 * Extracts the highest priority (lowest number) from the set of roles
 * @param roles
 */
export const extractHighestRolePriority = (roles: RoleModel[]): number | undefined => {
  if (!roles.length) return;

  const rolePriorities = roles
    .filter((r): r is RoleModel & { priority: number } => r.priority !== undefined)
    .map((r) => r.priority);

  if (!rolePriorities.length) return;

  return Math.min(...rolePriorities);
};
